import React from 'react';
import Header from '../homepage/Header';
import Footer from '../homepage/Footer';
import './index.css';

function PrivacyPolicy() {
  return (
    <div className="position-relative">
      <Header />
      <div className="privacy-policy-container">
        <section className="privacy-policy container">
          <h1 className="mb-4">SHOOT PRIVACY POLICY</h1>
          <p className="last-updated mb-4">Last Updated: January 7, 2026</p>

          <p className="mb-4">
            This Privacy Policy explains how Shoot Software Corporation (Shoot Software, Inc.),
            {' '}
            a Georgia company (&quot;Shoot,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;)
            {' '}
            collects, uses, discloses, and otherwise processes information when you access
            or use our Services, including https://www.trustshoot.com and related applications.
          </p>

          <p className="mb-4">
            This Privacy Policy also explains privacy rights and choices that may be
            available to you.
          </p>

          <p className="mb-5">
            If you do not agree with this Privacy Policy, do not use the Services.
          </p>

          <h2 className="mb-3">1. CONTACT US</h2>
          <p className="mb-2">Privacy email: privacy@trustshoot.com</p>
          <p className="mb-2">Legal email: legal@trustshoot.com</p>
          <p className="mb-2">Legal Mailing Address:</p>
          <p className="mb-2">Shoot Software Corporation (Shoot Software, Inc.)</p>
          <p className="mb-2">1799 Briarcliff Rd.</p>
          <p className="mb-2">#133237</p>
          <p className="mb-2">Atlanta, GA 30333</p>
          <p className="mb-5">United States</p>

          <h2 className="mb-3">2. HOW THIS POLICY WORKS WITH OUR TERMS AND DPA</h2>
          <p className="mb-5">
            Your use of the Services is governed by our Terms of Service.
            Where Shoot processes personal data on behalf of a Customer
            (for example, an employer using the Services for workforce monitoring),
            the Shoot Data Processing Addendum (&quot;DPA&quot;) may apply between Shoot and that Customer.
            {' '}
            <br />
            <br />
            If a separate HIPAA Business Associate Agreement (&quot;BAA&quot;) is executed,
            it governs PHI to the extent applicable.
          </p>

          <h2 className="mb-3">3. WHO IS WHO: CUSTOMER VS. END USER</h2>
          <p className="mb-4">
            The Services are typically used by organizations (each, a &quot;Customer&quot;).
            Individuals who use the Services under a Customer&apos;s account
            (including employees and contractors) are &quot;End Users.&quot;
          </p>

          <h3 className="mb-3">3.1 When Shoot acts as a processor/service provider</h3>
          <p className="mb-2">
            When a Customer configures the Services to collect or generate information
            about End Users (including monitoring outputs), the Customer typically
            determines the purposes and means of processing. In those cases:
          </p>
          <ul className="mb-3">
            <li>Customer is the controller (or business), and</li>
            <li>
              Shoot is the processor (or service provider), processing Customer Content
              on Customer&apos;s instructions.
            </li>
          </ul>
          <p className="mb-4">
            If you are an End User, your privacy questions about what is enabled
            and how your employer uses data should generally be directed to your employer/Customer first.
          </p>

          <h3 className="mb-3">3.2 When Shoot acts as a controller/business</h3>
          <p className="mb-2">
            Shoot acts as a controller/business for information we process for our own purposes, such as:
          </p>
          <ul className="mb-5">
            <li>Website visitor analytics,</li>
            <li>Marketing and sales communications,</li>
            <li>Account administration and billing,</li>
            <li>Security and fraud prevention.</li>
          </ul>

          <h2 className="mb-3">4. INFORMATION WE COLLECT</h2>
          <p className="mb-4">
            We collect information in several ways: (a) you provide it,
            (b) we collect it automatically, and (c) we receive it from
            third parties (e.g., integrations) at your direction.
            {' '}
            <br />
            <br />
            The specific data collected depends on your role (Customer admin vs. End User),
            the device or app used, and which features a Customer enables.
          </p>

          <h3 className="mb-3">4.1 Information you provide</h3>
          <ul className="mb-4">
            <li>
              <strong>Account and profile information:</strong>
              {' '}
              name, email address, phone number,
              username, role/permissions, team/department identifiers.
            </li>
            <li>
              <strong>Customer administration:</strong>
              {' '}
              organization name, admin contacts,
              user roster data uploaded by Customer.
            </li>
            <li>
              <strong>Billing and subscription information:</strong>
              {' '}
              billing contact info, invoices,
              payment status, and subscription entitlements. Payment card data is
              typically processed by a payment processor and not stored by us in full.
            </li>
            <li>
              <strong>Communications:</strong>
              {' '}
              support tickets, emails, chat messages with us,
              and other communications and attachments you submit.
            </li>
          </ul>

          <h3 className="mb-3">4.2 Information we collect automatically (website/app/device)</h3>
          <ul className="mb-4">
            <li>
              <strong>Device and network data:</strong>
              {' '}
              IP address, device identifiers, operating system,
              browser type, app version, language/time zone, and related technical identifiers.
            </li>
            <li>
              <strong>Usage and log data:</strong>
              {' '}
              pages/screens viewed, features used, timestamps,
              referring URLs, error logs, diagnostic logs, and performance data.
            </li>
            <li>
              <strong>Cookies and similar technologies:</strong>
              {' '}
              see Section 8.
            </li>
          </ul>

          <h3 className="mb-3">
            4.3 Monitoring, productivity, and security telemetry (as configured by Customer)
          </h3>
          <p className="mb-2">
            Depending on features enabled by Customer, the Services may collect or generate:
          </p>
          <ul className="mb-3">
            <li>
              <strong>Time tracking and attendance:</strong>
              {' '}
              time entries, start/stop times, duration,
              breaks, shifts, approvals, and related metadata.
            </li>
            <li>
              <strong>Activity signals:</strong>
              {' '}
              indicators derived from device input activity
              (e.g., mouse/keyboard activity levels and idle time detection).
              The Services are designed to measure activity signals and
              are not intended to capture the content of what is typed.
            </li>
            <li>
              <strong>Application and system context:</strong>
              {' '}
              active application/window context,
              application usage, running process/application metadata,
              and related system events needed to provide monitoring and reporting features.
            </li>
            <li>
              <strong>Website/URL activity:</strong>
              {' '}
              visited domains/URLs and related timestamps
              (where enabled), and optional browser-related activity data depending
              on configuration and device/app capabilities.
            </li>
            <li>
              <strong>Screenshots/screen capture:</strong>
              {' '}
              images of an End User&apos;s screen captured
              at intervals or events configured by Customer, with associated metadata
              (time, device, user, project/job context). Screenshots may incidentally
              capture sensitive information displayed on-screen.
            </li>
            <li>
              <strong>Location and job site data:</strong>
              {' '}
              GPS coordinates, routes, job site visits,
              geofence events, and related timestamps where enabled (e.g., mobile features).
            </li>
            <li>
              <strong>Unusual activity/security insights:</strong>
              {' '}
              signals, rules, alerts, and analytics
              intended to help Customers understand unusual patterns or potential risks (as configured).
            </li>
          </ul>
          <p className="mb-4">
            <strong>CUSTOMER RESPONSIBILITY:</strong>
            {' '}
            Customers are responsible for providing any
            required workplace notices and obtaining any required consents or authorizations
            for monitoring, consistent with applicable law.
          </p>

          <h3 className="mb-3">4.4 Information from integrations and third parties</h3>
          <p className="mb-5">
            If a Customer connects third-party services (such as SSO, HR/payroll, ticketing,
            messaging, or project systems), we may receive information from those systems as
            authorized by Customer and the integration settings.
          </p>

          <h2 className="mb-3">5. HOW WE USE INFORMATION</h2>
          <p className="mb-2">We use information for purposes such as:</p>
          <ul className="mb-5">
            <li>
              Providing and operating the Services, including authentication,
              account management, and feature delivery.
            </li>
            <li>
              Processing Customer Content on Customer&apos;s instructions
              (including workforce monitoring outputs where enabled).
            </li>
            <li>Customer support, troubleshooting, and responding to inquiries.</li>
            <li>Billing, invoicing, subscription management, and contract administration.</li>
            <li>
              Security, fraud prevention, abuse detection, and protecting rights and safety.
            </li>
            <li>
              Improving and developing the Services, including analytics and
              performance monitoring.
            </li>
            <li>
              Communicating about the Services, including service notices, updates,
              and (where permitted) marketing messages.
            </li>
          </ul>

          <h2 className="mb-3">6. HOW WE DISCLOSE INFORMATION</h2>
          <p className="mb-3">We may disclose information as follows:</p>

          <h3 className="mb-3">6.1 To Customers and their admins</h3>
          <p className="mb-4">
            If you are an End User, Customer admins and other authorized roles
            may access Customer Content, including monitoring outputs, depending
            on Customer&apos;s configuration and permissions.
          </p>

          <h3 className="mb-3">6.2 To service providers (subprocessors)</h3>
          <p className="mb-4">
            We use service providers for hosting, infrastructure, analytics,
            customer support tooling, communications delivery, and payment processing.
            They may process information only to provide services to us under contractual safeguards.
          </p>

          <h3 className="mb-3">6.3 At your direction (integrations)</h3>
          <p className="mb-4">
            We may disclose information when Customer enables integrations or
            requests we share data with a third party.
          </p>

          <h3 className="mb-3">6.4 For legal and safety reasons</h3>
          <p className="mb-4">
            We may disclose information to comply with law, respond to lawful requests,
            enforce our agreements, protect rights and safety, and investigate fraud or security incidents.
          </p>

          <h3 className="mb-3">6.5 Business transfers</h3>
          <p className="mb-4">
            Information may be transferred in connection with a merger, acquisition,
            reorganization, or sale of assets, subject to applicable law.
          </p>

          <h3 className="mb-3">6.6 Aggregated or de-identified information</h3>
          <p className="mb-5">
            We may disclose aggregated or de-identified information that does not reasonably identify you.
          </p>

          <h2 className="mb-3">7. DATA RETENTION (PLAN-BASED)</h2>
          <p className="mb-4">
            We retain information as long as necessary to provide the Services and
            for the purposes described in this Policy, unless a longer period is required
            or permitted by law.
            {' '}
            <br />
            <br />
            Because the Services involve monitoring and historical reporting,
            retention depends on the Customer&apos;s subscription plan, configuration,
            and any retention add-on(s). Customers should export or retrieve data
            they wish to keep before retention limits expire.
          </p>

          <h3 className="mb-3">7.1 Retention schedule (typical plan-based structure)</h3>
          <p className="mb-3">
            Unless otherwise agreed in writing, Customer data is retained according to a
            plan-based schedule similar to the following (retention periods are
            &quot;full historical periods,&quot; not necessarily a rolling day-by-day window):
          </p>

          <h4 className="mb-2">A) Detailed activity and screenshots (where enabled)</h4>
          <ul className="mb-3">
            <li>Free: 2 weeks</li>
            <li>Starter: 1 month</li>
            <li>Grow: 3 months</li>
            <li>Team: 6 months</li>
            <li>Enterprise: 6 months</li>
            <li>Retention add-on: 6 months</li>
          </ul>

          <h4 className="mb-2">B) App and URL activity metadata</h4>
          <ul className="mb-3">
            <li>All standard plans: 1 month</li>
            <li>Retention add-on: 1 year</li>
          </ul>

          <h4 className="mb-2">C) Location and job site visits (if enabled)</h4>
          <ul className="mb-3">
            <li>All standard plans: 1 month</li>
            <li>(May be configurable by Customer where supported)</li>
          </ul>

          <h4 className="mb-2">D) Core operational records (commonly needed for payroll/accounting/audit)</h4>
          <p className="mb-2">
            Examples: daily activity summaries, notes, time logs, invoices, approvals,
            time off, attendance shifts, expenses, work breaks, past payments, budget history, achievements
          </p>
          <ul className="mb-4">
            <li>All standard plans: 3 years</li>
            <li>Retention add-on: 6 years</li>
          </ul>

          <h3 className="mb-3">7.2 Backups and legal holds</h3>
          <p className="mb-5">
            We may retain limited copies in backups for a reasonable period,
            and may retain data longer if required by law, legal process, or legal hold.
          </p>

          <h2 className="mb-3">8. COOKIES AND SIMILAR TECHNOLOGIES</h2>
          <p className="mb-2">
            We use cookies and similar technologies (pixels, SDKs, local storage) to:
          </p>
          <ul className="mb-3">
            <li>Provide core site functionality,</li>
            <li>Remember preferences,</li>
            <li>Understand usage and improve performance,</li>
            <li>Measure marketing effectiveness where enabled.</li>
          </ul>
          <p className="mb-2">Your choices:</p>
          <ul className="mb-3">
            <li>You can control cookies through browser settings.</li>
            <li>Where required by law, we provide choices for non-essential cookies.</li>
          </ul>
          <p className="mb-5">
            <strong>Do Not Track:</strong>
            {' '}
            Some browsers offer &quot;Do Not Track&quot; signals.
            There is no uniform standard; we respond to such signals as required by applicable law.
          </p>

          <h2 className="mb-3">9. SECURITY</h2>
          <p className="mb-5">
            We use reasonable administrative, technical, and organizational safeguards
            designed to protect information. No system is completely secure.
            You are responsible for protecting credentials and using the Services securely.
          </p>

          <h2 className="mb-3">10. INTERNATIONAL TRANSFERS</h2>
          <p className="mb-5">
            We are based in the United States and may process and store information
            in the U.S. and other locations where we or our service providers operate.
            Where required, we use appropriate safeguards for cross-border transfers
            (for example, Standard Contractual Clauses, where applicable).
          </p>

          <h2 className="mb-3">11. YOUR PRIVACY RIGHTS AND CHOICES</h2>
          <p className="mb-3">Rights vary by location and context.</p>

          <h3 className="mb-3">11.1 End Users under a Customer account</h3>
          <p className="mb-4">
            If you use the Services through an employer/Customer, that Customer controls
            most Customer Content. Many requests (access, deletion, correction) must be
            handled by the Customer. We will assist Customers as required by law and contract.
          </p>

          <h3 className="mb-3">11.2 Requests to Shoot</h3>
          <p className="mb-5">
            Where Shoot acts as a controller (e.g., marketing or account admin data),
            you may request access, correction, deletion, or other rights by emailing
            privacy@trustshoot.com. We may need to verify your identity and authority.
          </p>

          <h2 className="mb-3">12. U.S. STATE PRIVACY DISCLOSURES (INCLUDING CALIFORNIA)</h2>
          <p className="mb-4">
            If applicable law provides rights (such as the right to know/access, delete,
            correct, or opt out of certain disclosures), you may exercise them by emailing
            privacy@trustshoot.com.
            {' '}
            <br />
            <br />
            We do not sell personal information in the ordinary sense. If we use cookies or
            similar technologies for analytics/marketing, some jurisdictions may treat certain
            disclosures as &quot;sharing&quot; for targeted advertising. You may request to opt out by
            contacting privacy@trustshoot.com, and where available, by using cookie controls.
          </p>

          <h2 className="mb-3">13. CHILDREN</h2>
          <p className="mb-5">
            The Services are intended for business use and are not directed to children.
            We do not knowingly collect personal information from children under 13
            (or under the minimum age required by applicable law).
          </p>

          <h2 className="mb-3">14. CHANGES TO THIS POLICY</h2>
          <p className="mb-5">
            We may update this Privacy Policy from time to time. We will update the
            &quot;Last Updated&quot; date at the top. If changes are material, we will provide
            additional notice where required by law.
          </p>

          <h2 className="mb-3">15. CONTACT</h2>
          <p className="mb-2">Privacy: privacy@trustshoot.com</p>
          <p className="mb-2">Legal: legal@trustshoot.com</p>
          <p className="mb-2">Mail:</p>
          <p className="mb-2">Shoot Software Corporation (Shoot Software, Inc.)</p>
          <p className="mb-2">1799 Briarcliff Rd.</p>
          <p className="mb-2">#133237</p>
          <p className="mb-2">Atlanta, GA 30333</p>
          <p className="mb-5">United States</p>
        </section>
      </div>
      <Footer />
    </div>
  );
}

export default PrivacyPolicy;
